<script setup>
    import { watch, onMounted, ref } from 'vue'

    import { Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from '@headlessui/vue'
    import { MagnifyingGlassIcon } from '@heroicons/vue/20/solid'
    import { storeToRefs } from 'pinia'
    import { useGoogleMapsStore } from '@/Stores'
    import { factory } from '@/Validators'

    const googleMapsStore = useGoogleMapsStore()

    const { googleMapsLoader, googleMapsApiAutocompleteSessionToken, googleMapsApiAutocompleteService, googleMapsApiPlacesService } = storeToRefs(googleMapsStore)

    const props = defineProps({
        modelValue: {
            type: String
        },
        validity: {
            type: String
        },
        placeholder: {
            type: String,
            default: 'Start typing an address...'
        },
        input_class: {
            type: String,
            default: 'border-stone-400 text-gray-900 placeholder:text-stone-400 focus:ring-lifeworx-blue-600 focus:border-lifeworx-blue-600'
        },
        validate: {
            type: Array,
            default: () => []
        }
    })

    const emit = defineEmits(['update:modelValue'])

    const addressSearchInput = ref(null)
    const searchInput = ref('')
    const ignoreInputChange = ref(false)
    const searchResults = ref([])
    const selectedPlaceResult = ref()
    const isValid = ref(true)

    watch(() => props.modelValue, (value) => {
        if (props.validate.includes('nullable') && !value) {
            isValid.value = true
            return;
        }

        for (let i in props.validate) {
            const validator = factory(props.validate[i])

            if (validator.isValid(value) == false) {
                isValid.value = false
                return;
            }

            isValid.value = true
        }
    }, { immediate: true })


    function displaySuggestions(predictions, status) {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
            searchResults.value = []
            return;
        }
        searchResults.value = predictions
    }

    function selectPlaceCallback(place, status) {
        if (status == google.maps.places.PlacesServiceStatus.OK) {
            selectedPlaceResult.value = place
            emit('update:modelValue', { addressString: searchInput.value, place: place })
            searchResults.value = []
        }
    }

    function selectPlace(place) {

        if (!place) {
            emit('update:modelValue', searchInput.value)
            return;
        }

        ignoreInputChange.value = true

        googleMapsApiPlacesService.value.getDetails({
            placeId: place.place_id,
            fields: ['address_components', 'formatted_address', 'geometry'],
            sessionToken: googleMapsApiAutocompleteSessionToken.value
        }, selectPlaceCallback)

        ignoreInputChange.value = false
    }

    onMounted(async () => {
        const { PlacesService, AutocompleteService, AutocompleteSessionToken } = await googleMapsLoader.value.importLibrary('places')

        googleMapsApiAutocompleteSessionToken.value = new AutocompleteSessionToken()
        googleMapsApiAutocompleteService.value = new AutocompleteService()
        googleMapsApiPlacesService.value = new PlacesService(document.createElement("div"))

        watch(searchInput, (newValue) => {
            emit('update:modelValue', newValue)
            if (newValue && !ignoreInputChange.value) {
                googleMapsApiAutocompleteService.value.getPlacePredictions({
                    input: searchInput.value,
                    componentRestrictions: { country: "us" },
                    types: ['address'],
                    sessionToken: googleMapsApiAutocompleteSessionToken.value
                }, displaySuggestions)
            }
        })
    })
</script>

<template>
    <Combobox
        as="div"
        :modelValue="props.modelValue"
        @update:modelValue="selectPlace"
        nullable
    >
        <div class="relative">
            <ComboboxInput
                ref="addressSearchInput"
                :class="[
                    'w-full rounded-md focus:outline-none px-2.5 py-1.5 sm:text-sm placeholder:text-stone-400',
                    props.input_class,
                    !isValid ? 'border-lifeworx-red-500 focus:ring-1 focus:border-lifeworx-red-500 focus:ring-lifeworx-red-500 bg-lifeworx-red-500 bg-opacity-10' : 'border border-stone-400 focus:ring-1 focus:ring-lifeworx-blue-600 focus:border-lifeworx-blue-600'
                ]"
                :placeholder="props.placeholder"
                @change="searchInput = $event.target.value"
                autocomplete="new-password"
            />

            <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                <MagnifyingGlassIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
            </ComboboxButton>

            <ComboboxOptions
                class="absolute z-popover mt-1 max-h-60 min-w-fit w-auto overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            >
                <ComboboxOption
                    v-for="(result, i) in searchResults"
                    :key="i"
                    :value="result"
                    as="template"
                    v-slot="{ active }"
                >
                    <li
                        :class="[
                            'cursor-pointer select-none rounded-md px-4 py-2',
                            active && 'bg-lifeworx-blue-600 bg-opacity-20 text-lifeworx-blue-800'
                        ]"
                    >
                        {{ result.description }}
                    </li>
                </ComboboxOption>
            </ComboboxOptions>
        </div>
    </Combobox>
</template>
